import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Search, Plus, Trash2, Edit2, Check, X, ChevronLeft, ChevronRight, Package, Filter, RotateCcw, Trophy, ExternalLink, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SellerProducts() {
  const { token, user } = useAuth();
  const [stats, setStats] = useState({ total: 0, active: 0, pending: 0, inactive: 0, store: null });
  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogResults, setCatalogResults] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [addPrice, setAddPrice] = useState("");
  const [addStock, setAddStock] = useState("100");
  const [addCargo, setAddCargo] = useState("3");
  const [addStockCode, setAddStockCode] = useState("");
  const [addBarcode, setAddBarcode] = useState("");
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualForm, setManualForm] = useState({
    name: "", oem_code: "", brand: "", price: "", stock: "",
    barcode: "", stock_code: "", cargo_time: "3",
    quantity_unit: "Adet", kdv_rate: "20"
  });
  const [manualImages, setManualImages] = useState([]);
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [duplicateCheck, setDuplicateCheck] = useState(null);
  const [duplicateLoading, setDuplicateLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };


  const handleManualImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setManualImages(prev => [...prev, { file, preview: ev.target.result, base64: ev.target.result }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeManualImage = (idx) => {
    setManualImages(prev => prev.filter((_, i) => i !== idx));
  };

  const checkDuplicates = async () => {
    if (!manualForm.oem_code.trim() && !manualForm.name.trim()) {
      toast.error("OEM kodu veya urun adi gereklidir");
      return;
    }
    setDuplicateLoading(true);
    setDuplicateCheck(null);
    try {
      const res = await fetch(`${API_URL}/api/seller/check-duplicates`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          product_name: manualForm.name,
          oem_code: manualForm.oem_code,
          brand: manualForm.brand,
        })
      });
      const data = await res.json();
      setDuplicateCheck(data);
      if (data.is_exact_match) {
        toast.error("Ayni urun katalogda mevcut! Katalogdan ekleyin.");
      } else if (data.duplicates && data.duplicates.length > 0) {
        toast.warning("Benzer urunler bulundu, kontrol edin.");
      } else {
        toast.success("Katalogda benzer urun bulunamadi.");
      }
    } catch (e) {
      toast.error("Kontrol sirasinda hata olustu");
    }
    setDuplicateLoading(false);
  };

  const submitManualProduct = async () => {
    const { name, oem_code, brand, price, stock, barcode, stock_code, cargo_time, kdv_rate, quantity_unit } = manualForm;
    if (!name || !oem_code || !brand || !price || !stock || !barcode || !stock_code) {
      toast.error("Tum zorunlu alanlari doldurun");
      return;
    }
    if (manualImages.length < 2) {
      toast.error("En az 2 resim yuklemelisiniz");
      return;
    }
    setManualSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/seller/manual-product`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          name, oem_code, brand,
          price: parseFloat(price),
          stock: parseInt(stock),
          barcode, stock_code,
          cargo_time: parseInt(cargo_time || "3"),
          kdv_rate: parseFloat(kdv_rate || "20"),
          quantity_unit: quantity_unit || "Adet",
          images: manualImages.map(img => img.base64)
        })
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Urun basariyla eklendi: " + data.model_code);
        setShowManualModal(false);
        setManualForm({ name: "", oem_code: "", brand: "", price: "", stock: "", barcode: "", stock_code: "", cargo_time: "3", quantity_unit: "Adet", kdv_rate: "20" });
        setManualImages([]);
        fetchListings();
        fetchStats();
      } else {
        toast.error(data.detail || "Hata olustu");
      if (res.status === 409) {
        // Duplicate found - refresh duplicate check
        setDuplicateCheck({ duplicates: [], ai_analysis: data.detail, is_exact_match: true });
      }
      }
    } catch (e) {
      toast.error("Baglanti hatasi");
    } finally {
      setManualSubmitting(false);
    }
  };


  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/seller/dashboard/stats`, { headers });
      if (res.ok) { const d = await res.json(); setStats(prev => ({ ...prev, ...d })); }
    } catch (err) {}
    try {
      const res = await fetch(`${API_URL}/api/my-store`, { headers });
      if (res.ok) { const s = await res.json(); setStats(prev => ({ ...prev, store: s })); }
    } catch (err) {}
  }, [token]);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      const res = await fetch(`${API_URL}/api/seller/listings?${params}`, { headers });
      const data = await res.json();
      let items = data.listings || [];
      if (statusFilter === "active") items = items.filter(i => i.is_active !== false);
      else if (statusFilter === "inactive") items = items.filter(i => i.is_active === false);
      if (query) {
        const q = query.toLowerCase();
        items = items.filter(i => (i.product_name||"").toLowerCase().includes(q) || (i.oem_code||i.oem_number||"").toLowerCase().includes(q) || (i.brand||"").toLowerCase().includes(q) || (i.seller_barcode||"").toLowerCase().includes(q) || (i.seller_stock_code||"").toLowerCase().includes(q) || (i.model_code||"").toLowerCase().includes(q));
      }
      setListings(items);
      setTotal(data.total || 0);
      setPages(data.pages || 0);
      const active = (data.listings || []).filter(i => i.is_active !== false).length;
      const inactive = (data.listings || []).filter(i => i.is_active === false).length;
      setStats(prev => ({ ...prev, total: data.total || 0, active, inactive, pending: 0 }));
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [token, page, limit, statusFilter, query]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchListings(); }, [fetchListings]);

  const handleSearch = (e) => { e?.preventDefault(); setQuery(searchInput); setPage(1); };
  const clearFilters = () => { setSearchInput(""); setQuery(""); setStatusFilter("all"); setPage(1); };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditData({ price: item.price, stock: item.stock, cargo_time: item.cargo_time || 3, seller_stock_code: item.seller_stock_code || "", seller_barcode: item.seller_barcode || "" });
  };

  const saveEdit = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/seller/listings/${id}`, {
        method: "PUT", headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(editData)
      });
      if (res.ok) { setEditingId(null); fetchListings(); toast.success("Guncellendi"); }
      else { const d = await res.json(); toast.error(d.detail || "Hata"); }
    } catch (e) { toast.error("Guncelleme hatasi"); }
  };

  const toggleActive = async (item) => {
    try {
      await fetch(`${API_URL}/api/seller/listings/${item.id}`, {
        method: "PUT", headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ is_active: !item.is_active })
      });
      fetchListings();
    } catch (e) {}
  };

  const deleteListing = async (id) => {
    if (!window.confirm("Bu urunu listeden kaldirmak istediginize emin misiniz?")) return;
    try { await fetch(`${API_URL}/api/seller/listings/${id}`, { method: "DELETE", headers }); fetchListings(); toast.success("Urun kaldirildi"); }
    catch (e) { toast.error("Silinemedi"); }
  };

  const searchCatalog = async () => {
    if (catalogSearch.length < 2) return;
    try {
      const res = await fetch(`${API_URL}/api/seller/search-products?q=${encodeURIComponent(catalogSearch)}`, { headers });
      const data = await res.json();
      setCatalogResults(Array.isArray(data) ? data : data.products || []);
    } catch (e) {}
  };

  const addListing = async () => {
    if (!selectedProduct || !addPrice) return;
    if (!addStockCode.trim() || !addBarcode.trim()) { toast.error("Stok kodu ve barkod zorunludur"); return; }
    try {
      const res = await fetch(`${API_URL}/api/seller/listings`, {
        method: "POST", headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          product_id: selectedProduct.id,
          price: parseFloat(addPrice),
          stock: parseInt(addStock) || 100,
          cargo_time: parseInt(addCargo) || 3,
          seller_stock_code: addStockCode.trim(),
          seller_barcode: addBarcode.trim(),
        })
      });
      if (res.ok) {
        setShowAddModal(false); setSelectedProduct(null); setCatalogSearch(""); setCatalogResults([]);
        setAddPrice(""); setAddStockCode(""); setAddBarcode("");
        fetchListings(); fetchStats(); toast.success("Urun eklendi");
      } else { const d = await res.json(); toast.error(d.detail || "Eklenemedi"); }
    } catch (e) { toast.error("Ekleme hatasi"); }
  };

  const getImg = (item) => {
    const img = item.product_image || item.image_url || "";
    if (!img) return "https://placehold.co/60x60/f5f5f5/999?text=-";
    if (Array.isArray(img)) return img[0] || "https://placehold.co/60x60/f5f5f5/999?text=-";
    return img.split("|")[0];
  };

  const statusTabs = [
    { key: "all", label: "Tum Urunler", count: stats.total, color: "text-orange-600 border-orange-500" },
    { key: "active", label: "Aktif Urunler", count: stats.active, color: "text-green-600 border-green-500" },
    { key: "inactive", label: "Pasif Urunler", count: stats.inactive, color: "text-gray-500 border-gray-400" },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-[#1a1a2e] text-white">
        <div className="max-w-[1400px] mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <span className="font-bold text-lg">Satici Paneli</span>
            <nav className="flex items-center gap-4 text-sm">
              <span className="text-orange-400 font-medium">Urun</span>
              <span className="text-gray-400">Siparis & Kargo</span>
              <span className="text-gray-400">Finans</span>
              <span className="text-gray-400">Raporlar</span>
            </nav>
          </div>
          <div className="text-sm text-gray-300">{stats.store?.name || user?.name || "Magaza"}</div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Urun Yonetimi</h1>
            <div className="text-xs text-gray-500 mt-1">Toplam <strong className="text-gray-700">{stats.total}</strong> urun</div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowAddModal(true)} className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white text-sm px-4 py-2 rounded transition" data-testid="add-product-btn">
              <Plus size={16} /> Katalogdan Ekle
            </button>
            <button onClick={() => setShowManualModal(true)} className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded transition" data-testid="manual-product-btn">
              <Edit2 size={16} /> Manuel Urun Ekle
            </button>
          </div>
        </div>

        {/* Status Tabs */}
        <div className="bg-white rounded-t-lg border border-b-0">
          <div className="flex">
            {statusTabs.map(tab => (
              <button key={tab.key} onClick={() => { setStatusFilter(tab.key); setPage(1); }}
                className={`px-5 py-3 text-sm font-medium border-b-2 transition ${statusFilter === tab.key ? tab.color : "text-gray-500 border-transparent hover:text-gray-700"}`}>
                {tab.label} <span className="text-xs ml-1">({tab.count})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border px-4 py-3">
          <form onSubmit={handleSearch} className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="text-[10px] text-gray-500 font-medium block mb-1">OEM / Barkod / Urun Adi / Model Kodu / Stok Kodu</label>
              <input type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)}
                placeholder="Arayiniz..." className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:border-orange-400" />
            </div>
            <button onClick={clearFilters} type="button" className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 px-3 py-2 border rounded">
              <RotateCcw size={14} /> Temizle
            </button>
            <button type="submit" className="flex items-center gap-1 bg-[#1a1a2e] text-white text-sm px-5 py-2 rounded hover:bg-[#252542] transition">
              <Filter size={14} /> Filtrele
            </button>
          </form>
        </div>

        {/* Table */}
        <div className="bg-white border border-t-0 rounded-b-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b bg-gray-50">
            <span className="text-xs text-gray-500">Tum Urunler</span>
            <div className="flex items-center gap-3 text-xs">
              <span className="text-gray-500">Her Sayfada</span>
              <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }} className="border rounded px-2 py-1 text-xs">
                <option value={20}>20</option><option value={50}>50</option><option value={100}>100</option>
              </select>
              <span className="text-gray-500">Urun(ler)</span>
              <div className="flex items-center gap-1 ml-2">
                <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="p-1 border rounded disabled:opacity-30"><ChevronLeft size={14} /></button>
                <span className="w-7 h-7 flex items-center justify-center bg-orange-500 text-white rounded text-xs font-medium">{page}</span>
                <button onClick={() => setPage(Math.min(pages || 1, page + 1))} disabled={page >= (pages || 1)} className="p-1 border rounded disabled:opacity-30"><ChevronRight size={14} /></button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-xs text-gray-500 font-medium">
                  <th className="text-left px-3 py-2.5 w-[240px]">Urun Bilgisi</th>
                  <th className="text-left px-2 py-2.5">OEM</th>
                  <th className="text-left px-2 py-2.5">Barkod</th>
                  <th className="text-left px-2 py-2.5">Model Kodu</th>
                  <th className="text-left px-2 py-2.5">Stok Kodu</th>
                  <th className="text-center px-2 py-2.5">Durum</th>
                  <th className="text-right px-2 py-2.5">Satis Fiyati</th>
                  <th className="text-center px-2 py-2.5">Stok</th>
                  <th className="text-right px-2 py-2.5">BuyBox Fiyati</th>
                  <th className="text-center px-2 py-2.5">BuyBox</th>
                  <th className="text-center px-2 py-2.5">Termin</th>
                  <th className="text-center px-2 py-2.5 w-[80px]">Islem</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={12} className="text-center py-16">
                    <div className="w-8 h-8 border-3 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" />
                  </td></tr>
                ) : listings.length === 0 ? (
                  <tr><td colSpan={12} className="text-center py-16">
                    <Package size={48} className="mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500 font-medium">Urun Bulunamadi</p>
                    <p className="text-gray-400 text-xs mt-1">Urun eklemek icin "Urun Ekle" butonunu kullanin</p>
                  </td></tr>
                ) : listings.map(item => (
                  <tr key={item.id} className="border-b hover:bg-gray-50 transition">
                    {/* Urun Bilgisi - tiklanabilir */}
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <img src={getImg(item)} alt="" className="w-10 h-10 object-contain rounded border p-0.5 shrink-0"
                          onError={e => { e.target.src = "https://placehold.co/60x60/f5f5f5/999?text=-"; }} />
                        <div className="min-w-0">
                          <a href={`/products/${item.product_id}`} target="_blank" rel="noopener noreferrer"
                            className="text-xs font-medium text-blue-700 hover:text-blue-900 hover:underline line-clamp-2 cursor-pointer" data-testid="product-link">
                            {item.product_name}
                          </a>
                          <p className="text-[10px] text-gray-400 mt-0.5">{item.brand || "-"}</p>
                        </div>
                      </div>
                    </td>
                    {/* OEM */}
                    <td className="px-2"><span className="text-xs text-gray-600">{item.oem_code || item.oem_number || "-"}</span></td>
                    {/* Barkod */}
                    <td className="px-2">
                      {editingId === item.id ? (
                        <input type="text" value={editData.seller_barcode || ""} onChange={e => setEditData({ ...editData, seller_barcode: e.target.value })}
                          className="w-24 border rounded px-2 py-1 text-xs" />
                      ) : (
                        <span className="text-xs text-gray-600">{item.seller_barcode || "-"}</span>
                      )}
                    </td>
                    {/* Model Kodu */}
                    <td className="px-2"><span className="text-xs text-blue-600 font-medium">{item.model_code || "-"}</span></td>
                    {/* Stok Kodu */}
                    <td className="px-2">
                      {editingId === item.id ? (
                        <input type="text" value={editData.seller_stock_code || ""} onChange={e => setEditData({ ...editData, seller_stock_code: e.target.value })}
                          className="w-24 border rounded px-2 py-1 text-xs" />
                      ) : (
                        <span className="text-xs text-gray-600">{item.seller_stock_code || "-"}</span>
                      )}
                    </td>
                    {/* Durum */}
                    <td className="text-center px-2">
                      <button onClick={() => toggleActive(item)}
                        className={`text-[10px] font-medium px-2 py-1 rounded-full ${item.is_active !== false ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
                        {item.is_active !== false ? "Aktif" : "Pasif"}
                      </button>
                    </td>
                    {/* Satis Fiyati */}
                    <td className="text-right px-2">
                      {editingId === item.id ? (
                        <input type="number" value={editData.price} onChange={e => setEditData({ ...editData, price: parseFloat(e.target.value) })}
                          className="w-24 border rounded px-2 py-1 text-xs text-right" />
                      ) : (
                        <span className="text-xs font-semibold text-gray-800">{item.price?.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} TL</span>
                      )}
                    </td>
                    {/* Stok */}
                    <td className="text-center px-2">
                      {editingId === item.id ? (
                        <input type="number" value={editData.stock} onChange={e => setEditData({ ...editData, stock: parseInt(e.target.value) })}
                          className="w-16 border rounded px-2 py-1 text-xs text-center" />
                      ) : (
                        <span className={`text-xs font-medium ${item.stock > 0 ? "text-green-600" : "text-red-500"}`}>{item.stock}</span>
                      )}
                    </td>
                    {/* BuyBox Fiyati */}
                    <td className="text-right px-2">
                      {item.buybox_price != null ? (
                        <span className="text-xs font-semibold text-gray-700">{item.buybox_price?.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} TL</span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    {/* BuyBox Durumu */}
                    <td className="text-center px-2">
                      {item.buybox_winner ? (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                          <Trophy size={10} /> Kazanan
                        </span>
                      ) : item.seller_count > 0 ? (
                        <span className="text-[10px] text-gray-400">{item.seller_count} satici</span>
                      ) : (
                        <span className="text-[10px] text-gray-400">-</span>
                      )}
                    </td>
                    {/* Termin */}
                    <td className="text-center px-2">
                      {editingId === item.id ? (
                        <input type="number" value={editData.cargo_time} onChange={e => setEditData({ ...editData, cargo_time: parseInt(e.target.value) })}
                          className="w-12 border rounded px-1 py-1 text-xs text-center" />
                      ) : (
                        <span className="text-xs text-gray-500">{item.cargo_time || 3} gun</span>
                      )}
                    </td>
                    {/* Islemler */}
                    <td className="text-center px-2">
                      <div className="flex items-center justify-center gap-1">
                        {editingId === item.id ? (
                          <>
                            <button onClick={() => saveEdit(item.id)} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check size={14} /></button>
                            <button onClick={() => setEditingId(null)} className="p-1 text-gray-400 hover:bg-gray-100 rounded"><X size={14} /></button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => startEdit(item)} className="p-1 text-blue-500 hover:bg-blue-50 rounded" title="Duzenle"><Edit2 size={14} /></button>
                            <button onClick={() => deleteListing(item.id)} className="p-1 text-red-400 hover:bg-red-50 rounded" title="Sil"><Trash2 size={14} /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50 text-xs">
              <span className="text-gray-500">Toplam {total} urun</span>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="px-2 py-1 border rounded bg-white disabled:opacity-30"><ChevronLeft size={14} /></button>
                {Array.from({ length: Math.min(pages, 7) }, (_, i) => {
                  let pg;
                  if (pages <= 7) pg = i + 1;
                  else if (page <= 4) pg = i + 1;
                  else if (page >= pages - 3) pg = pages - 6 + i;
                  else pg = page - 3 + i;
                  return (
                    <button key={pg} onClick={() => setPage(pg)}
                      className={`w-7 h-7 rounded text-xs font-medium ${pg === page ? "bg-orange-500 text-white" : "border bg-white text-gray-600 hover:bg-gray-50"}`}>{pg}</button>
                  );
                })}
                <button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page >= pages} className="px-2 py-1 border rounded bg-white disabled:opacity-30"><ChevronRight size={14} /></button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Product Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h2 className="font-bold text-gray-900">Urun Ekle</h2>
              <button onClick={() => { setShowAddModal(false); setSelectedProduct(null); setCatalogResults([]); setCatalogSearch(""); setAddStockCode(""); setAddBarcode(""); }} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-5 overflow-y-auto flex-1 space-y-4">
              {!selectedProduct ? (
                <>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Katalogdan Urun Ara</label>
                    <div className="flex gap-2">
                      <input type="text" value={catalogSearch} onChange={e => setCatalogSearch(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && searchCatalog()}
                        placeholder="OEM kodu, urun adi, marka... (tire, virgul farketmez)" className="flex-1 border rounded px-3 py-2 text-sm focus:outline-none focus:border-orange-400" data-testid="catalog-search-input" />
                      <button onClick={searchCatalog} className="bg-orange-500 text-white px-4 rounded hover:bg-orange-600" data-testid="catalog-search-btn"><Search size={16} /></button>
                    </div>
                  </div>
                  <div className="max-h-[300px] overflow-y-auto space-y-1">
                    {catalogResults.map(p => (
                      <button key={p.id} onClick={() => { setSelectedProduct(p); setAddPrice(String(p.display_price || p.price || "")); }}
                        className="w-full flex items-center gap-3 p-2 rounded border hover:border-orange-400 hover:bg-orange-50 transition text-left">
                        <img src={(p.images && p.images[0]) || p.image_url || "https://placehold.co/40x40/f5f5f5/999?text=-"} alt=""
                          className="w-10 h-10 object-contain rounded border p-0.5" onError={e => { e.target.src = "https://placehold.co/40x40/f5f5f5/999?text=-"; }} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-800 truncate">{p.name}</p>
                          <p className="text-[10px] text-gray-400">{p.brand} | OEM: {p.oem_number || p.oem_code || "-"} | {p.model_code || ""}</p>
                        </div>
                      </button>
                    ))}
                    {catalogResults.length === 0 && catalogSearch.length >= 2 && (
                      <p className="text-center text-gray-400 text-sm py-6" data-testid="catalog-no-results">Sonuc bulunamadi. Farkli arama terimleri deneyin.</p>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{selectedProduct.name}</p>
                      <p className="text-xs text-gray-500">{selectedProduct.brand} | OEM: {selectedProduct.oem_number || selectedProduct.oem_code || "-"}</p>
                      {selectedProduct.model_code && <p className="text-xs text-blue-600 font-medium mt-0.5">Model Kodu: {selectedProduct.model_code}</p>}
                    </div>
                    <button onClick={() => setSelectedProduct(null)} className="ml-auto text-gray-400 hover:text-red-500"><X size={16} /></button>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Satis Fiyati (TL) *</label>
                      <input type="number" value={addPrice} onChange={e => setAddPrice(e.target.value)}
                        className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:border-orange-400" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Stok Adedi</label>
                      <input type="number" value={addStock} onChange={e => setAddStock(e.target.value)}
                        className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:border-orange-400" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Termin (Gun)</label>
                      <input type="number" value={addCargo} onChange={e => setAddCargo(e.target.value)}
                        className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:border-orange-400" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-red-600 block mb-1">Stok Kodu * (zorunlu)</label>
                      <input type="text" value={addStockCode} onChange={e => setAddStockCode(e.target.value)}
                        placeholder="Sizin stok kodunuz" className="w-full border border-red-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-orange-400" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-red-600 block mb-1">Barkod * (zorunlu)</label>
                      <input type="text" value={addBarcode} onChange={e => setAddBarcode(e.target.value)}
                        placeholder="Sizin barkod numaraniz" className="w-full border border-red-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-orange-400" />
                    </div>
                  </div>
                </>
              )}
            </div>
            {selectedProduct && (
              <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2">
                <button onClick={() => { setShowAddModal(false); setSelectedProduct(null); setAddStockCode(""); setAddBarcode(""); }} className="px-4 py-2 text-sm text-gray-600 border rounded hover:bg-gray-100">Iptal</button>
                <button onClick={addListing} className="px-5 py-2 text-sm bg-orange-500 text-white rounded hover:bg-orange-600 transition">Urun Ekle</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Manuel Urun Ekle Modal */}
      {showManualModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={(e) => e.target === e.currentTarget && setShowManualModal(false)}>
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center rounded-t-xl">
              <h2 className="font-bold text-gray-900 text-lg">Manuel Urun Ekle</h2>
              <button onClick={() => setShowManualModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              {/* Urun Adi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Urun Adi *</label>
                <input type="text" value={manualForm.name} onChange={e => setManualForm({...manualForm, name: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-300 focus:border-orange-400 outline-none"
                  placeholder="Ornek: HAVA FILTRESI CERATO 06-11" data-testid="manual-name" />
              </div>

              {/* OEM + Marka */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">OEM Kodu *</label>
                  <input type="text" value={manualForm.oem_code} onChange={e => setManualForm({...manualForm, oem_code: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-300 focus:border-orange-400 outline-none"
                    placeholder="28113-2F800" data-testid="manual-oem" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Marka *</label>
                  <input type="text" value={manualForm.brand} onChange={e => setManualForm({...manualForm, brand: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-300 focus:border-orange-400 outline-none"
                    placeholder="MANN" data-testid="manual-brand" />
                </div>
              </div>

              {/* AI Duplicate Check */}
              <button onClick={checkDuplicates} disabled={duplicateLoading || (!manualForm.oem_code.trim() && !manualForm.name.trim())}
                className="w-full flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm py-2.5 rounded-lg border transition disabled:opacity-50" data-testid="check-duplicates-btn">
                {duplicateLoading ? (<><Loader2 size={14} className="animate-spin" /> AI ile kontrol ediliyor...</>) : (<><Search size={14} /> Katalogda Benzer Urun Kontrol Et (AI)</>)}
              </button>

              {duplicateCheck && duplicateCheck.duplicates && duplicateCheck.duplicates.length > 0 && (
                <div className={`rounded-lg p-4 ${duplicateCheck.is_exact_match ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'}`} data-testid="duplicate-warning">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={18} className={duplicateCheck.is_exact_match ? 'text-red-500 mt-0.5 flex-shrink-0' : 'text-yellow-500 mt-0.5 flex-shrink-0'} />
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${duplicateCheck.is_exact_match ? 'text-red-700' : 'text-yellow-700'}`}>
                        {duplicateCheck.is_exact_match ? 'Ayni urun katalogda mevcut!' : 'Benzer urunler bulundu'}
                      </p>
                      <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap">{duplicateCheck.ai_analysis}</p>
                      <div className="mt-2 space-y-1">
                        {duplicateCheck.duplicates.slice(0, 5).map((d, i) => (
                          <div key={i} className="flex items-center gap-2 bg-white rounded p-2 text-xs border">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-800 truncate">{d.name}</p>
                              <p className="text-gray-500">OEM: {d.oem_code} | {d.brand} | {d.model_code}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {duplicateCheck && duplicateCheck.ai_analysis && (!duplicateCheck.duplicates || duplicateCheck.duplicates.length === 0) && (
                <div className="rounded-lg p-3 bg-green-50 border border-green-200 text-sm text-green-700">{duplicateCheck.ai_analysis}</div>
              )}

              {/* Fiyat + Stok + KDV */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Satis Fiyati (TL) *</label>
                  <input type="number" step="0.01" value={manualForm.price} onChange={e => setManualForm({...manualForm, price: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-300 focus:border-orange-400 outline-none"
                    placeholder="150.00" data-testid="manual-price" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stok Adedi *</label>
                  <input type="number" value={manualForm.stock} onChange={e => setManualForm({...manualForm, stock: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-300 focus:border-orange-400 outline-none"
                    placeholder="50" data-testid="manual-stock" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">KDV Orani (%)</label>
                  <select value={manualForm.kdv_rate} onChange={e => setManualForm({...manualForm, kdv_rate: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-300 focus:border-orange-400 outline-none bg-white" data-testid="manual-kdv">
                    <option value="1">%1</option>
                    <option value="10">%10</option>
                    <option value="20">%20</option>
                  </select>
                </div>
              </div>

              {/* Barkod + Stok Kodu + Kargo */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Barkod *</label>
                  <input type="text" value={manualForm.barcode} onChange={e => setManualForm({...manualForm, barcode: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-300 focus:border-orange-400 outline-none"
                    placeholder="8680000123456" data-testid="manual-barcode" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stok Kodu *</label>
                  <input type="text" value={manualForm.stock_code} onChange={e => setManualForm({...manualForm, stock_code: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-300 focus:border-orange-400 outline-none"
                    placeholder="SK-12345" data-testid="manual-stockcode" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kargo Suresi (Gun)</label>
                  <input type="number" value={manualForm.cargo_time} onChange={e => setManualForm({...manualForm, cargo_time: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-300 focus:border-orange-400 outline-none"
                    placeholder="3" data-testid="manual-cargo" />
                </div>
              </div>

              {/* Adet Birimi */}
              <div className="w-1/3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Adet Birimi</label>
                <select value={manualForm.quantity_unit} onChange={e => setManualForm({...manualForm, quantity_unit: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-300 focus:border-orange-400 outline-none bg-white" data-testid="manual-unit">
                  <option value="Adet">Adet</option>
                  <option value="Takim">Takim</option>
                  <option value="Cift">Cift</option>
                  <option value="Paket">Paket</option>
                  <option value="Metre">Metre</option>
                  <option value="Kg">Kg</option>
                  <option value="Litre">Litre</option>
                </select>
              </div>

              {/* Resim Yukleme */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Urun Resimleri * (En az 2 resim)</label>
                <div className="flex flex-wrap gap-3 mb-2">
                  {manualImages.map((img, idx) => (
                    <div key={idx} className="relative w-20 h-20 rounded-lg border overflow-hidden group">
                      <img src={img.preview} alt={""} className="w-full h-full object-cover" />
                      <button onClick={() => removeManualImage(idx)}
                        className="absolute inset-0 bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                  <label className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-orange-400 hover:bg-orange-50 transition">
                    <Plus size={24} className="text-gray-400" />
                    <input type="file" accept="image/*" multiple onChange={handleManualImageUpload} className="hidden" data-testid="manual-image-input" />
                  </label>
                </div>
                <p className="text-xs text-gray-400">{manualImages.length}/2 resim yuklendi {manualImages.length < 2 && "(en az 2 gerekli)"}</p>
              </div>

              {/* Butonlar */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button onClick={() => { setShowManualModal(false); setManualImages([]); setDuplicateCheck(null); setManualForm({ name: "", oem_code: "", brand: "", price: "", stock: "", barcode: "", stock_code: "", cargo_time: "3", quantity_unit: "Adet", kdv_rate: "20" }); }}
                  className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-100">Iptal</button>
                <button onClick={submitManualProduct} disabled={manualSubmitting || (duplicateCheck && duplicateCheck.is_exact_match)}
                  className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50" data-testid="manual-submit-btn">
                  {manualSubmitting ? "Ekleniyor..." : "Urunu Ekle"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
